/**
 * Responsive Design Constants
 *
 * Touch target sizes based on accessibility guidelines:
 * - WCAG 2.2 Success Criterion 2.5.8: Minimum 24x24 CSS pixels
 * - Apple Human Interface Guidelines: 44x44pt recommended
 * - Material Design: 48x48dp recommended
 *
 * We use 44px as our minimum for tablet/touch devices as a balance
 * between accessibility and screen real estate.
 */

/**
 * Minimum touch target size for interactive elements (buttons, inputs, etc.)
 * Based on Apple HIG recommendation of 44pt
 */
export const TOUCH_TARGET_MIN_SIZE = 44; // px

/**
 * Comfortable touch target size for frequently used elements
 * Based on Material Design recommendation of 48dp
 */
export const TOUCH_TARGET_COMFORTABLE_SIZE = 48; // px

/**
 * Breakpoints for responsive design
 * Aligned with MUI default breakpoints
 */
export const RESPONSIVE_BREAKPOINTS = {
  /** Mobile devices (phones) */
  mobile: 600, // xs-sm boundary
  /** Tablet devices */
  tablet: 900, // sm-md boundary
  /** Desktop devices */
  desktop: 1200 // md-lg boundary
} as const;

/**
 * Media query strings for use in MUI theme component overrides
 *
 * Usage in theme overrides:
 * ```ts
 * MuiButton: {
 *   styleOverrides: {
 *     root: {
 *       [MEDIA_QUERIES.touch]: {
 *         minHeight: 44
 *       }
 *     }
 *   }
 * }
 * ```
 */
export const MEDIA_QUERIES = {
  /** Targets mobile devices (max-width: 599px) */
  mobile: `@media (max-width: ${RESPONSIVE_BREAKPOINTS.mobile - 1}px)`,

  /** Targets tablet devices and below (max-width: 1199px) */
  tablet: `@media (max-width: ${RESPONSIVE_BREAKPOINTS.desktop - 1}px)`,

  /** Targets touch-enabled devices (coarse pointer like finger) */
  touch: '@media (pointer: coarse)',

  /** Targets devices with hover capability (mouse, trackpad) */
  hover: '@media (hover: hover)',

  /** Targets devices without hover capability (touch screens) */
  noHover: '@media (hover: none)',

  /** Combined: touch device OR tablet-sized screen */
  touchOrTablet: `@media (pointer: coarse), (max-width: ${RESPONSIVE_BREAKPOINTS.desktop - 1}px)`
} as const;

/**
 * Responsive sizing values for common components
 * Desktop values match existing theme, tablet values are touch-optimized
 */
export const RESPONSIVE_SIZES = {
  /** Input field heights */
  inputHeight: {
    desktop: 32,
    tablet: 44
  },

  /** Button heights */
  buttonHeight: {
    desktop: 29,
    tablet: 44
  },

  /** Icon button dimensions (square) */
  iconButtonSize: {
    desktop: 32,
    tablet: 44
  },

  /** Checkbox and Radio button sizes */
  checkboxSize: {
    desktop: 18,
    tablet: 24
  },

  /** SVG icon sizes */
  iconSize: {
    desktop: 14,
    tablet: 20
  },

  /** Base font sizes */
  fontSize: {
    desktop: 14,
    tablet: 16 // 16px prevents iOS zoom on input focus
  },

  /** Padding for interactive elements */
  padding: {
    desktop: 8,
    tablet: 12
  },

  /** Menu item heights */
  menuItemHeight: {
    desktop: 32,
    tablet: 44
  },

  /** Chip heights */
  chipHeight: {
    desktop: 24,
    tablet: 36
  }
} as const;

/**
 * Type definitions for responsive sizes
 */
export type ResponsiveSize = {
  desktop: number;
  tablet: number;
};

export type ResponsiveSizeKey = keyof typeof RESPONSIVE_SIZES;
